import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Table, Tag, Typography } from 'antd';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import { formatDateTime } from '../Assessments/shared';
import { fetchComputeEvents, startCompute, stopCompute } from './api';
import { readableError } from './errors';
import { describeActor, useNow } from './Jobs';
import { ComputeLaneEvent, ComputeRefusal, ComputeSnapshot } from './types';

/**
 * The corpus-backed compute lane: whether it is running, the control that
 * starts it, and an honest account of the wait.
 *
 * Four things this screen refuses to do.
 *
 * It never shows a bare spinner for a start that takes two to four minutes —
 * a spinner with no steps and no clock is indistinguishable from a hang.
 *
 * It never greys out ordinary work because this lane is down: a curated draft
 * mints with no corpus and no model at all, so the lane being stopped is
 * irrelevant to most of the catalogue, and the copy says so.
 *
 * It never reduces a refusal to a sentence of its own invention. The server
 * names every one — no backend configured, no corpus database, a stack that
 * must be restarted, a task role that may read ECS but not scale it, a
 * platform that answered with a failure — and each arrives with its own
 * message, its own remedy and its own blockers, in every state.
 *
 * And it never hides the Stop button over something that is still billing.
 * A start can be abandoned from its first second; a lane whose row says
 * stopped while the platform still holds a task says so in red and offers the
 * control that ends it.
 */

export const COMPUTE_TITLES: Record<string, string> = {
  unavailable: 'Scenario compute is not available in this environment.',
  stopped: 'Scenario compute is stopped.',
  starting: 'Scenario compute is starting.',
  running: 'Scenario compute is running.',
  stopping: 'Waiting for the task to exit.',
};

export const UNAVAILABLE_BODY =
  'Curated drafts still mint here; they need no corpus and no model. These are the reasons the corpus-backed work cannot run:';

export const RUNNING_WITHOUT_CORPUS_COPY =
  'Scenario compute is running without the corpus. Uncurated mints will render with no filler news and will be labelled as such in their report. Curated mints are unaffected.';

/** Past this, "usually ready in 2–3 minutes" has stopped being true and the
 *  card says so rather than letting the clock quietly run on. */
export const SLOW_START_SECONDS = 300;

/**
 * The start has been given up on: no worker ever reported in, the deadline
 * passed, and the service is scaling itself back to zero. Different news from
 * "this is taking a while", and the card must not keep promising a container
 * that is not coming.
 */
export const isTimedOut = (snapshot: ComputeSnapshot | null) =>
  Boolean(snapshot?.start_timed_out);

/**
 * The row says stopped and the platform is still holding a task. The shape
 * that bills unnoticed: `backend.scale()` succeeded and its transaction rolled
 * back, or a stop was issued mid-placement. Whenever it is true a Stop is
 * offered, whatever the derived state reads.
 */
export const hasPlatformDrift = (snapshot: ComputeSnapshot | null) =>
  Boolean(snapshot?.drift?.platform_still_running);

export const computeState = (snapshot: ComputeSnapshot | null) =>
  String(snapshot?.state || 'unavailable');

/** A lane that is up but cannot reach the corpus is its own situation: the
 *  work runs, it just runs without filler. It is derived, never stored. */
export const isRunningWithoutCorpus = (snapshot: ComputeSnapshot | null) =>
  computeState(snapshot) === 'running' &&
  snapshot?.capabilities?.corpus?.available === false;

export const TIMED_OUT_TITLE = 'Scenario compute failed to start.';

export const DRIFT_TITLE =
  'Scenario compute is stopped here, but the platform still has a task.';

export const computeHeadline = (snapshot: ComputeSnapshot | null) => {
  if (isTimedOut(snapshot)) return TIMED_OUT_TITLE;
  if (hasPlatformDrift(snapshot)) return DRIFT_TITLE;
  if (isRunningWithoutCorpus(snapshot)) return RUNNING_WITHOUT_CORPUS_COPY;
  return COMPUTE_TITLES[computeState(snapshot)] || COMPUTE_TITLES.unavailable;
};

export const computeTone = (
  snapshot: ComputeSnapshot | null,
): 'success' | 'warning' | 'info' | 'error' => {
  // A start that has been abandoned, and a lane billing for a task nobody
  // meant to keep, are the two states that cost money. Neither is amber.
  if (isTimedOut(snapshot) || hasPlatformDrift(snapshot)) return 'error';
  if (isRunningWithoutCorpus(snapshot)) return 'warning';
  const state = computeState(snapshot);
  if (state === 'running') return 'success';
  if (state === 'starting' || state === 'stopping') return 'warning';
  return 'info';
};

/** What the card says under a start's clock. */
export const startProgressCopy = (
  snapshot: ComputeSnapshot | null,
  elapsed: number,
): string => {
  if (isTimedOut(snapshot)) {
    const reason =
      snapshot?.start_deadline?.reason ||
      (snapshot?.notes || []).find(note => note.startsWith('No worker')) ||
      `No worker reported in within ${formatElapsed(snapshot?.start_deadline_seconds || 0)} of the start being requested.`;
    return `${reason} The service is being scaled back to zero; start it again once the cause is fixed.`;
  }
  return elapsed >= SLOW_START_SECONDS
    ? `This is taking longer than usual (${formatElapsed(elapsed)}).`
    : `Usually ready in 2–3 minutes. Elapsed ${formatElapsed(elapsed)}.`;
};

/** m:ss, because that is how a person reads a two-to-four minute wait. */
export const formatElapsed = (seconds: number): string => {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

/**
 * The lane's own clock. `elapsed_seconds` is measured by the server, and
 * the local tick carries it forward between polls so the number on screen
 * moves every second instead of jumping every three.
 */
export const elapsedSeconds = (
  snapshot: ComputeSnapshot | null,
  receivedAt: number,
  now: number,
) =>
  Number(snapshot?.elapsed_seconds || 0) +
  Math.max(0, (now - receivedAt) / 1000);

/** Slim-lane health is read straight off the job ledger; a queue that has
 *  not moved in five minutes means the ordinary worker, not this lane. */
export const SLIM_BACKLOG_WARNING_SECONDS = 300;

export const slimWarning = (snapshot: ComputeSnapshot | null): string => {
  const slim = snapshot?.slim;
  if (!slim || !slim.pending_jobs) return '';
  if ((slim.oldest_pending_seconds || 0) < SLIM_BACKLOG_WARNING_SECONDS)
    return '';
  return `${slim.pending_jobs} ${slim.pending_jobs === 1 ? 'job has' : 'jobs have'} been waiting for the ordinary worker for ${formatElapsed(slim.oldest_pending_seconds)}. That worker is not this service; check that the on-demand queue is being consumed.`;
};

/* ------------------------------- the strip ------------------------------ */

/**
 * One line above every tab. It exists because the answer to "why did my
 * mint not start" is usually up here, and an admin should not have to go
 * looking for it.
 */
export const LaneStrip: React.FC<{
  snapshot: ComputeSnapshot | null;
  error?: string;
  onOpenCompute?: () => void;
}> = ({ snapshot, error, onOpenCompute }) => {
  if (error) {
    return (
      <Alert
        className="assessment-callout"
        type="warning"
        showIcon
        title="The compute service state could not be read."
        description={error}
      />
    );
  }
  if (!snapshot) return null;
  const backlog = slimWarning(snapshot);
  const blocked = snapshot.blocked_jobs || 0;
  return (
    <Alert
      className="assessment-callout"
      data-testid="compute-lane-strip"
      showIcon
      type={backlog ? 'warning' : computeTone(snapshot)}
      title={computeHeadline(snapshot)}
      description={
        <Space size={12} wrap>
          {blocked > 0 && (
            <Typography.Text>
              {blocked} {blocked === 1 ? 'job is' : 'jobs are'} waiting for it
              to start.
            </Typography.Text>
          )}
          {backlog && <Typography.Text>{backlog}</Typography.Text>}
          {onOpenCompute && (
            <Typography.Link onClick={onOpenCompute}>
              Open the compute service
            </Typography.Link>
          )}
        </Space>
      }
    />
  );
};

/* ------------------------------- the card ------------------------------- */

const StartSteps: React.FC<{ snapshot: ComputeSnapshot }> = ({ snapshot }) => {
  const steps = snapshot.steps || [];
  if (!steps.length) {
    return (
      <Typography.Text type="secondary">
        The backend reported no readiness steps for this start.
      </Typography.Text>
    );
  }
  const since = snapshot.since ? new Date(snapshot.since).getTime() : null;
  return (
    <Space
      direction="vertical"
      size={4}
      style={{ width: '100%' }}
      data-testid="compute-steps"
    >
      {steps.map(step => {
        // `done` and `at` are what `compute._steps` emits. The card used to
        // read `done_at`, a key the server has never sent, so every step
        // rendered as pending however far the start had actually got.
        const done = Boolean(step.done);
        const doneAt = done && step.at ? new Date(step.at).getTime() : null;
        const took =
          doneAt && since ? formatElapsed((doneAt - since) / 1000) : null;
        return (
          <Space
            key={step.key}
            size={8}
            data-testid={`compute-step-${step.key}`}
            data-done={String(done)}
          >
            {done ? (
              <CheckCircleFilled style={{ color: '#52c41a' }} />
            ) : (
              <ClockCircleOutlined />
            )}
            <span>{step.label}</span>
            {took && (
              <Typography.Text type="secondary">at {took}</Typography.Text>
            )}
            {step.detail && (
              <Typography.Text type="secondary">{step.detail}</Typography.Text>
            )}
          </Space>
        );
      })}
    </Space>
  );
};

type Props = {
  snapshot: ComputeSnapshot | null;
  error?: string;
  loading?: boolean;
  onRefresh: () => void;
};

const ComputeService: React.FC<Props> = ({
  snapshot,
  error,
  loading,
  onRefresh,
}) => {
  const [busy, setBusy] = useState('');
  const [commandError, setCommandError] = useState('');
  const [refusal, setRefusal] = useState<ComputeRefusal | null>(null);
  const [resent, setResent] = useState(false);
  const [events, setEvents] = useState<ComputeLaneEvent[]>([]);
  const [receivedAt, setReceivedAt] = useState(() => Date.now());

  const state = computeState(snapshot);
  const timedOut = isTimedOut(snapshot);
  const drift = hasPlatformDrift(snapshot);
  const starting = state === 'starting';
  const stopping = state === 'stopping';
  const unavailable = state === 'unavailable';
  // Anything the platform may still be billing for can be stopped. The old
  // rule offered Stop only while the card read `running` or `starting`, so a
  // lane whose row said stopped over a live task — the one shape that bills
  // unnoticed — had no control at all. A task merely DRAINING from a stop we
  // already sent is not that case and keeps both controls disabled.
  const somethingToStop = state === 'running' || starting || drift;

  useEffect(() => {
    setReceivedAt(Date.now());
  }, [snapshot]);

  const now = useNow(1000, starting || stopping);
  const elapsed = elapsedSeconds(snapshot, receivedAt, now);

  const loadEvents = useCallback(async () => {
    try {
      const response = await fetchComputeEvents({ page_size: 25 });
      setEvents(response.results);
    } catch (_requestError) {
      // The event log is provenance, not control: a card that refuses to
      // render its Start button because history could not be read would be
      // the wrong failure.
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents, state]);

  const command = async (action: 'start' | 'stop') => {
    setBusy(action);
    setCommandError('');
    setRefusal(null);
    setResent(false);
    try {
      const response = await (action === 'start'
        ? startCompute()
        : stopCompute());
      if (response.refusal) setRefusal(response.refusal);
      if (response.resent) setResent(true);
      onRefresh();
      await loadEvents();
    } catch (requestError) {
      setCommandError(
        readableError(requestError, { what: 'The compute service' }),
      );
    } finally {
      setBusy('');
    }
  };

  const capabilities = snapshot?.capabilities || {};
  const corpus = capabilities.corpus;
  const llm = capabilities.llm;
  const blockers = snapshot?.blockers || [];
  const backlog = slimWarning(snapshot);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title={snapshot?.display_name || 'Scenario corpus service'}
        data-testid="compute-card"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={onRefresh}
            >
              Refresh
            </Button>
            {/* Stop is enabled from the first second of a start — an admin
                who started it by mistake should not have to wait out the whole
                cold start to undo it — and from ANY state in which the
                platform may still be holding a task, including one the row
                already calls stopped. That last case is the one that billed
                until somebody opened the console. */}
            {somethingToStop && (
              <Button
                danger
                loading={busy === 'stop'}
                onClick={() => command('stop')}
              >
                Stop service
              </Button>
            )}
            <Button
              type="primary"
              // `can_start` is the server's own answer to "is Start offered":
              // it is false whenever the backend named a blocker, which is a
              // wider set than the states that read `unavailable`.
              disabled={
                unavailable ||
                starting ||
                stopping ||
                state === 'running' ||
                snapshot?.can_start === false
              }
              loading={busy === 'start'}
              onClick={() => command('start')}
            >
              Start service
            </Button>
          </Space>
        }
      >
        {error && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={error}
          />
        )}
        {commandError && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={commandError}
          />
        )}

        <Alert
          showIcon
          type={computeTone(snapshot)}
          title={computeHeadline(snapshot)}
          description={
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {unavailable && (
                <>
                  <span>{UNAVAILABLE_BODY}</span>
                  {/* The environment's own reasons. A refusal's blockers are
                      shown on the refusal panel below, under the sentence they
                      belong to, rather than quietly replacing these. */}
                  <ul data-testid="compute-blockers">
                    {blockers.map(blocker => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </>
              )}
              {starting && (
                <>
                  <Typography.Text type="secondary">
                    Requested by {describeActor(snapshot?.requested_by)} at{' '}
                    {formatDateTime(snapshot?.since)}
                  </Typography.Text>
                  {snapshot && <StartSteps snapshot={snapshot} />}
                  <Typography.Text data-testid="compute-elapsed">
                    {startProgressCopy(snapshot, elapsed)}
                  </Typography.Text>
                </>
              )}
              {stopping && !drift && (
                <Typography.Text type="secondary">
                  Both controls are disabled until the task has exited.
                </Typography.Text>
              )}
              {state === 'running' && (
                <Space direction="vertical" size={2}>
                  <Typography.Text type="secondary">
                    Running since {formatDateTime(snapshot?.since)}, started by{' '}
                    {describeActor(snapshot?.requested_by)}.
                  </Typography.Text>
                  {snapshot?.worker?.image_tag && (
                    <Typography.Text type="secondary">
                      Image {String(snapshot.worker.image_tag)}
                    </Typography.Text>
                  )}
                  <Typography.Text data-testid="compute-capabilities">
                    Corpus: {corpus?.available ? 'connected' : 'not attached'} ·
                    Model:{' '}
                    {llm?.available
                      ? llm.detail || 'connected'
                      : 'not resolved'}
                  </Typography.Text>
                </Space>
              )}
              {drift && (
                <Space direction="vertical" size={2}>
                  <Typography.Text data-testid="compute-drift">
                    The lane was asked to stop but the platform still holds a
                    task for it.
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    The platform still wants{' '}
                    {snapshot?.drift?.desired_count ?? 0}{' '}
                    {(snapshot?.drift?.desired_count ?? 0) === 1
                      ? 'task'
                      : 'tasks'}{' '}
                    for this lane and is running{' '}
                    {snapshot?.drift?.running_count ?? 0}. Press Stop to tell it
                    again; it bills until the task exits.
                  </Typography.Text>
                </Space>
              )}
              {(state === 'stopped' || timedOut) &&
                snapshot?.last_error?.message && (
                  <Typography.Text type="secondary">
                    Last error: {String(snapshot.last_error.message)}
                    {snapshot?.last_error?.remedy
                      ? ` ${String(snapshot.last_error.remedy)}`
                      : ''}
                  </Typography.Text>
                )}
            </Space>
          }
        />

        {/* Shown in EVERY state. It used to be suppressed while the lane read
            `unavailable`, which is exactly the state a developer stack sits in
            — so the one sentence carrying the terminal command that fixes it
            was the one sentence the admin never saw. */}
        {refusal && (
          <Alert
            className="assessment-callout"
            data-testid="compute-refusal"
            type="warning"
            showIcon
            title={refusal.message}
            description={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {refusal.remedy && (
                  <Typography.Text>{refusal.remedy}</Typography.Text>
                )}
                {refusal.blockers.length > 0 && (
                  <ul style={{ margin: 0 }}>
                    {refusal.blockers.map(blocker => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                )}
                <Typography.Text type="secondary">
                  Refused by {refusal.code}.
                </Typography.Text>
              </Space>
            }
          />
        )}

        {resent && (
          <Alert
            className="assessment-callout"
            type="info"
            showIcon
            title="The platform was still holding a task for this lane. It has been told to stop again."
          />
        )}

        <Card className="assessment-inner-card" size="small" title="Queues">
          <Space direction="vertical" size={4}>
            <Typography.Text>
              {snapshot?.blocked_jobs || 0} job
              {(snapshot?.blocked_jobs || 0) === 1 ? '' : 's'} waiting for this
              service to start.
            </Typography.Text>
            <Typography.Text>
              {snapshot?.slim?.pending_jobs || 0} job
              {(snapshot?.slim?.pending_jobs || 0) === 1 ? '' : 's'} waiting for
              the ordinary worker
              {snapshot?.slim?.pending_jobs
                ? `, the oldest for ${formatElapsed(snapshot.slim.oldest_pending_seconds || 0)}`
                : ''}
              .
            </Typography.Text>
            {backlog && <Alert type="warning" showIcon title={backlog} />}
          </Space>
        </Card>
      </Card>

      <Card title="Service history" size="small">
        <Typography.Paragraph type="secondary">
          One row per change of state — who started or stopped the service, and
          when it went quiet. Heartbeats are not recorded; only transitions are.
        </Typography.Paragraph>
        <Table
          rowKey="id"
          className="assessment-table"
          size="small"
          tableLayout="fixed"
          dataSource={events}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          scroll={{ x: 860 }}
          columns={[
            {
              title: 'When',
              dataIndex: 'at',
              width: 190,
              className: 'assessment-cell-nowrap',
              render: (value: string) => formatDateTime(value),
            },
            {
              title: 'What happened',
              dataIndex: 'action',
              width: 190,
              render: (value: string) => (
                <Tag>
                  {String(value || '')
                    .replace(/_/g, ' ')
                    .toUpperCase()}
                </Tag>
              ),
            },
            {
              title: 'State',
              width: 190,
              render: (_: unknown, row: ComputeLaneEvent) => (
                <span>
                  {row.from_state} → {row.to_state}
                </span>
              ),
            },
            {
              title: 'Who',
              width: 150,
              render: (_: unknown, row: ComputeLaneEvent) =>
                describeActor(row.actor),
            },
            {
              title: 'Why',
              dataIndex: 'reason',
              render: (value: string) => value || '—',
            },
          ]}
        />
      </Card>
    </Space>
  );
};

export default ComputeService;
